import ColorStyles from '@Styles/colors.scss';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog } from '@mui/material';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@Components/Button/Button';
import LoadingButton from '@Components/LoadingButton/LoadingButton';
import { ModalBody, ModalHeader } from '@Components/ModalComponents';
import FoldersApi from '@Data/folders/Api';
import { selectedFolderState } from '@Data/folders/Reducer';
import { selectedProjectState } from '@Data/projects/Reducer';
import { QualityControlApi } from '@Data/qualityControl/Api';
import {
    inspectedRateState,
    qcStatusOptionsState,
    selectedAnalysisEntityState,
    yieldRateState,
} from '@Data/qualityControl/Reducer';
import TeamsApi from '@Data/teams/Api';
import { teamMembersState } from '@Data/teams/Reducer';
import { KoruRoutes } from '@Services/navigation/Paths';
import { Stats } from '../JobInfo/Stats';
import Styles from './OutroDialog.scss';

interface IOutroDialogProps {
    isOpen: boolean;
    onCancel: () => void;
}

enum SubmitState {
    PASS,
    REWORK,
}

export const OutroDialog = (props: IOutroDialogProps) => {
    const { isOpen, onCancel } = props;
    const { teamId, organizationId } = useParams();

    const selectedAnalysisEntity = useSelector(selectedAnalysisEntityState);
    const teamMembers = useSelector(teamMembersState);
    const statusOptions = useSelector(qcStatusOptionsState);
    const [isSubmitContainerOpen, setIsSubmitContainerOpen] = useState(false);

    const [submitState, setSubmitState] = useState<SubmitState>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const selectedFolder = useSelector(selectedFolderState);
    const selectedProject = useSelector(selectedProjectState);
    const { user } = useAuth0();

    const targetInspectionRate = selectedFolder?.target_inspection_rate;
    const targetYieldRate = selectedFolder?.target_pass_yield;

    const inspectedRate = useSelector(inspectedRateState);
    const yieldRate = useSelector(yieldRateState);

    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
            if (_isEmpty(teamMembers)) {
                TeamsApi.getTeamMembers(teamId);
            }
            if (_isEmpty(statusOptions) && !_isNil(selectedAnalysisEntity)) {
                QualityControlApi.getQcStatusOptions(
                    selectedAnalysisEntity?.entity_version_id
                );
            }
        }
    }, [isOpen, teamMembers, statusOptions, selectedAnalysisEntity]);

    const handleClickReworkPass = (isAllPass: boolean) => {
        setIsSubmitContainerOpen(true);
        setSubmitState(isAllPass ? SubmitState.PASS : SubmitState.REWORK);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        let result = await QualityControlApi.submitQcRun(
            selectedFolder.folder_id,
            selectedFolder.folder_name,
            selectedProject.project_id,
            user.sub,
            user.name,
            selectedAnalysisEntity.updated_by_id,
            selectedAnalysisEntity.updated_by_name,
            'NA',
            'NA',
            submitState === SubmitState.PASS,
            selectedAnalysisEntity.status,
            'NA',
            targetYieldRate,
            yieldRate,
            targetInspectionRate,
            inspectedRate
        );
        if (result === true) {
            QualityControlApi.reset();
            await FoldersApi.getFolders(selectedProject.project_id);
            navigate(
                KoruRoutes.qc.getUrl(
                    organizationId,
                    teamId,
                    selectedProject.project_id,
                    []
                )
            );
        }

        setIsSubmitting(false);
    };

    const handleClose = () => {
        setIsSubmitContainerOpen(false);
        setSubmitState(null);
    };

    const handleOpenIkeOfficeLink = () => {
        const departmentId = selectedProject?.department_id;
        const jobId = selectedFolder?.job_id;
        const ikeOfficeEntityLink = `https://office.ikegps.com/#/measure?d=${departmentId}&t=${jobId}`;
        window.open(ikeOfficeEntityLink, '_blank');
    };

    return (
        <Dialog id="outro-dialog" open={isOpen} onClose={handleClose}>
            <ModalHeader
                id="outro-dialog-header"
                title="Finish QC Inspection"
                onCancel={handleClose}
            />
            <ModalBody>
                <div className={Styles.outroDialogContainer}>
                    <Stats />
                    {!isSubmitContainerOpen && (
                        <div className={Styles.outroDialogButtonsContainer}>
                            <Button
                                color="red"
                                id="outro-dialog-back-button"
                                onClick={() => {
                                    setIsSubmitContainerOpen(false);
                                    onCancel();
                                }}
                            >
                                Back
                            </Button>
                            <div className={Styles.presubmitButtonsContainer}>
                                <Button
                                    color="blue"
                                    id="outro-dialog-rework-button"
                                    onClick={() => handleClickReworkPass(false)}
                                    disabled={isSubmitContainerOpen}
                                >
                                    Rework Job
                                </Button>
                                <Button
                                    color="green"
                                    id="outro-dialog-pass-button"
                                    onClick={() => handleClickReworkPass(true)}
                                    disabled={isSubmitContainerOpen}
                                >
                                    Pass Job
                                </Button>
                            </div>
                        </div>
                    )}
                    <div
                        className={Styles.submitComponentsContainer}
                        style={
                            isSubmitContainerOpen
                                ? {
                                      height: 'auto',
                                  }
                                : { height: '0px', padding: '0px' }
                        }
                    >
                        <div className={Styles.submitSubContainer}>
                            <div
                                className={Styles.passReworkIndicator}
                                style={
                                    submitState === SubmitState.PASS
                                        ? {
                                              color: `${ColorStyles.ikeSecondaryLightGreen}`,
                                          }
                                        : {
                                              color: `${ColorStyles.ikeSecondaryLightBlue}`,
                                          }
                                }
                            >
                                <span>
                                    {submitState === SubmitState.PASS
                                        ? 'Pass'
                                        : 'Rework'}
                                </span>
                            </div>
                        </div>
                        <div className={Styles.submitButtonsSubContainer}>
                            <Button
                                color="primary"
                                id="outro-dialog-io-button"
                                onClick={handleOpenIkeOfficeLink}
                                sx={{
                                    backgroundColor:
                                        ColorStyles.ikeSecondaryDarkBlue,
                                    fontWeight: '400',
                                    fontSize: '14px',
                                    lineHeight: '17px',
                                    padding: '.6rem 1rem',
                                    '&:hover': {
                                        backgroundColor:
                                            ColorStyles.ikeSecondaryLightBlue,
                                    },
                                }}
                            >
                                Update the Assigned User and Status in IKE
                                Office
                            </Button>
                        </div>
                        <div className={Styles.submitButtonsSubContainer}>
                            <Button
                                color="red"
                                id="outro-dialog-back-button"
                                onClick={handleClose}
                            >
                                Back
                            </Button>
                            <LoadingButton
                                color="green"
                                id="outro-dialog-submit-button"
                                onClick={handleSubmit}
                                loading={isSubmitting}
                                disabled={false}
                            >
                                Submit
                            </LoadingButton>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Dialog>
    );
};
